
/* ============================== src/components/cards/TaskCard.jsx ============================== */
// path: frontend/src/components/cards/TaskCard.jsx
/**
 * Карточка задачи в канбане. RBAC-флаги в контекстном меню —
 * capabilities через useAuth() (см. menus/taskMenu.js).
 */
import { memo } from "react";
import { Clickable } from "../Clickable";
import FloatingMenu from "../FloatingMenu";
import EmployeeCardLink from "../EmployeeCardLink";
import { UnreadBadge } from "../UnreadBadge";
import { PriorityStrip } from "../PriorityStrip";
import { TagsList } from "../TagsList";
import { FactPlan } from "../FactPlan";
import { buildTaskMenu } from "../menus/taskMenu";
import { PRIORITIES } from "../../utils/constants";
import { useAuth } from "../../hooks/useAuth";
import { TODAY, fmtDMY, daysDiff, humanizeDeadline } from "../../utils/date";
import { getProjectColor } from "../../utils/projectHelpers";
import { ColorDot, ColoredSpan } from "../ColoredSpan";

export const TaskCard = memo(function TaskCard({
  task,
  project,
  assignee,
  spent,
  unread,
  db,
  user,
  openTask,
  openTag,
  onMove,
  onDelete,
  onCopy,
  onMakeTemplate,
}) {
  const { capabilities } = useAuth();
  const isFinal = task.status === "closed" || task.status === "cancelled";

  const deadlineLabel = task.deadline ? humanizeDeadline(task.deadline) : null;

  const overdue = task.deadline && !isFinal && task.deadline < TODAY;

  const soon =
    task.deadline &&
    !overdue &&
    !isFinal &&
    daysDiff(TODAY, task.deadline) <= 3;

  const deadlineClass = overdue ? "kdl late" : soon ? "kdl soon" : "kdl";

  const priorityDef = PRIORITIES[task.priority] || {
    label: task.priority || "Нет",
    color: "#64748b",
  };

  const menuItems = buildTaskMenu({
    task,
    user,
    capabilities,
    db,
    openTask,
    onMove,
    onDelete,
    onCopy,
    onMakeTemplate,
  });

  const handleOpen = () => openTask(task.id);

  const hasTags = Array.isArray(task.tags) && task.tags.length > 0;

  return (
    <FloatingMenu
      items={menuItems}
      children={({ anchorProps }) => (
        <Clickable as="div" {...anchorProps} onClick={handleOpen}>
          <PriorityStrip color={priorityDef.color} />

          <div className="kcard-zone">
            <div className="kcard-title">{task.title}</div>
            {hasTags && (
              <TagsList
                tags={task.tags}
                onTagClick={openTag}
                interactive
                maxVisible={2}
              />
            )}
          </div>

          <div className="kcard-zone">
            <div className="kcard-proj">
              <ColorDot color={getProjectColor(project)} className="pdot" />
              {project?.code}
            </div>
            <div className="kcard-meta">
              {assignee ? (
                <div className="kcard-manager">
                  <span className="kcard-manager-avatar">
                    <EmployeeCardLink employee={assignee} size="xs" />
                  </span>
                  <span className="kcard-manager-name">
                    {assignee.first} {assignee.last}
                  </span>
                </div>
              ) : (
                <span />
              )}
              <FactPlan
                fact={spent}
                plan={task.plannedHours}
                color={priorityDef.color}
              />
            </div>
          </div>

          <div className="kcard-zone">
            <div className="kcard-foot">
              {deadlineLabel && (
                <span className={deadlineClass} title={fmtDMY(task.deadline)}>
                  {deadlineLabel}
                </span>
              )}
              <div className="kcard-foot-end">
                <ColoredSpan color={priorityDef.color} className="prio-chip">
                  {priorityDef.label}
                </ColoredSpan>
                {unread > 0 && (
                  <UnreadBadge
                    count={unread}
                    onClick={() => openTask(task.id, "chat")}
                    variant="neutral"
                  />
                )}
              </div>
            </div>
          </div>
        </Clickable>
      )}
    />
  );
});
